import { useTranslation } from '@jq4race/i18n';
import type { EventStaffAssignmentResponse, UserListItem } from '@jq4race/shared';
import {
  Badge,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from '@jq4race/ui';
import { UserRound } from 'lucide-react';

interface StaffTableProps {
  staff: EventStaffAssignmentResponse[];
  users: Map<string, UserListItem>;
  loading: boolean;
  onUnassign: (assignment: EventStaffAssignmentResponse) => void;
  unassigningUserId?: string | null;
  /** Unassign is hidden once the event is closed (R1.4/R3.4), mirroring the
   * backend UnassignStaffUseCase's closed-event guard. Defaults to true so
   * existing callers keep current behavior. */
  editable?: boolean;
}

const rowActionClass =
  'inline-flex h-8 items-center justify-center rounded border border-jq-danger/30 bg-jq-danger/5 px-3 text-[12px] font-medium text-jq-danger transition hover:bg-jq-danger/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 disabled:cursor-not-allowed disabled:opacity-50';

export function StaffTable({
  staff,
  users,
  loading,
  onUnassign,
  unassigningUserId,
  editable = true,
}: StaffTableProps) {
  const { t, i18n } = useTranslation('events');
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t('staffTable.name')}</TableHead>
          <TableHead>{t('staffTable.role')}</TableHead>
          <TableHead>{t('staffTable.assignedOn')}</TableHead>
          <TableHead className="w-[120px] text-right">{t('staffTable.actions')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {loading && staff.length === 0 ? (
          <SkeletonRows />
        ) : staff.length === 0 ? (
          <TableEmpty colSpan={4}>
            <EmptyState />
          </TableEmpty>
        ) : (
          staff.map((assignment) => {
            const user = users.get(assignment.userId);
            const displayName = user?.email ?? assignment.userId;
            const roleLabel =
              assignment.role ?? (user ? t(`constants.staffRole.${user.role}`) : '—');
            const isUnassigning = unassigningUserId === assignment.userId;
            return (
              <TableRow key={assignment.id}>
                <TableCell>
                  <span className="font-medium text-jq-charcoal">{displayName}</span>
                </TableCell>
                <TableCell>
                  <Badge variant="secondary">{roleLabel}</Badge>
                </TableCell>
                <TableCell className="text-jq-graphite">
                  {new Date(assignment.assignedAt).toLocaleDateString(i18n.language, {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                  })}
                </TableCell>
                <TableCell className="text-right">
                  {editable ? (
                    <button
                      type="button"
                      onClick={() => onUnassign(assignment)}
                      disabled={isUnassigning}
                      className={rowActionClass}
                      aria-label={t('staffTable.unassignAriaLabel', { name: displayName })}
                    >
                      {isUnassigning ? t('staffTable.removing') : t('staffTable.unassign')}
                    </button>
                  ) : null}
                </TableCell>
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );
}

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 3 }).map((_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: skeletons are positional
        <TableRow key={`skeleton-${i}`}>
          {Array.from({ length: 4 }).map((_, j) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: skeletons are positional
            <TableCell key={`cell-${j}`}>
              <Skeleton className="h-4 w-full" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

function EmptyState() {
  const { t } = useTranslation('events');
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
      <UserRound className="h-10 w-10 text-jq-stone" strokeWidth={1.25} />
      <p className="text-[14px] font-medium text-jq-charcoal">{t('staffTable.empty.title')}</p>
      <p className="max-w-sm text-[13px] text-jq-graphite">{t('staffTable.empty.description')}</p>
    </div>
  );
}
